<template>
  <div class="formSearchWrapper" v-click-outside="() => close()">
    <label class="formLabel" :for="`tag-items-${_uid}`" v-if="showLabel">{{ label }}</label>
    <div class="formSearch--ChildTags">
      <div class="formSearch__InputWrapper" v-if="multiple || (!multiple && tagSelection.length === 0)">
        <icon class="formSearch__Icon" :name="icon"></icon>
        <p class="formSearch__Loader" v-if="loading">{{ $store.getters.getStringTranslation('loading') }}...</p>
        <input
          :id="`tag-items-${_uid}`"
          class="formInput--Search"
          type="text"
          @keyup="keyUp()"
          @click="getTagOptions(), (focused = true)"
          v-model="search"
          :placeholder="$store.getters.getStringTranslation(placeholder.replace(/\s/g, '').toLowerCase())"
        />
      </div>
      <div class="formSearch formSearch--TagsList" v-if="tagSelection.length > 0">
        <template>
          <template v-for="tag in tagSelection">
            <span class="formSearch__Item" :key="tag.id" v-if="getTitle(tag)">
              {{ getTitle(tag) }}
              <button type="button" @click="removeTag(tag)">
                <icon weight="regular" name="times"></icon>
              </button>
            </span>
          </template>
        </template>
      </div>

      <ul
        class="formSearch__List formSearch__List--ChildTags"
        :class="{ 'is-Active': focused === true }"
        v-if="tagOptions !== undefined"
      >
        <template v-if="searchResults.length > 0 && search.length > 0">
          <li class="formSearch__ListItem" v-for="tag in searchResults" :key="tag.id">
            <span @click="pushTag(tag)">
              {{ getTitle(tag) }}
            </span>
          </li>
        </template>
        <template v-else>
          <li
            :class="['formSearch__ListItem tagListItem', { 'tagListItem--Active': activeParents.includes(tag.id) }]"
            v-for="tag in tagOptions"
            :key="tag.id"
          >
            <div class="tagListItem__Inner">
              <span
                :class="['tagListItem__ParentTag', { 'tagListItem__ParentTag--Selected': ids.includes(tag.id) }]"
                @click="pushTag(tag)"
              >
                {{ getTitle(tag) }}
              </span>
              <div class="tagListItem__Icon" @click="getSubtags(tag), toggleDropdown(tag.id)">
                <icon name="caret-down"></icon>
              </div>
            </div>
            <div :class="['tagListItem__SubTags', { 'tagListItem__SubTags--Active': activeParents.includes(tag.id) }]">
              <ul
                class="tagListItem__SubList tagListItem__SubTag"
                v-if="subTags.filter((t) => t.parent_id === tag.id).length > 0"
              >
                <li
                  :class="['formSearch__ListItem', { 'tagListItem__ChildTag--Selected': ids.includes(child.id) }]"
                  v-for="child in subTags.filter((t) => t.parent_id === tag.id)"
                  :key="child.id"
                >
                  <span class="tagListItem__ChildTag" @click="pushTag(tag, child)">
                    {{ getTitle(child) }}
                  </span>
                </li>
              </ul>
              <p
                class="tagListItem__NoResults"
                v-if="!loading && subTags.filter((t) => t.parent_id === tag.id).length === 0"
              >
                No sub tags to select
              </p>
            </div>
          </li>
        </template>
        <li class="formSearch__ListItem" v-if="noResults === true">
          <span>{{ errorMessage }}</span>
        </li>
        <li class="formSearch__ListItem" v-else-if="tagOptions.length === 0">
          <span>{{ $store.getters.getStringTranslation('loading') }}...</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import debounce from 'tiny-debounce'
import HTTP from '@/http-common.js'

export default {
  props: {
    types: {
      type: Array,
      default: function () {
        return []
      },
    },
    lang: {
      type: String,
      default: 'en',
    },
    defaultTags: {
      type: Number,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    user: {
      type: Object,
      default: function () {
        return {}
      },
    },
    action: {
      type: String,
      default: null,
    },
    tags: {},
    showLabel: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Search by name',
    },
    label: {
      type: String,
      default: 'Tags',
    },
    errorMessage: {
      type: String,
      default: 'No tags found',
    },
    icon: {
      type: String,
      default: 'search',
    },
    limit: {
      default: 100,
    },
  },
  name: 'SearchTag',
  data() {
    return {
      search: '',
      ids: [],
      tagSelection: [],
      tagOptions: [],
      loading: false,
      loadingSubTags: false,
      noResults: false,
      focused: false,
      subTags: [],
      activeParents: [],
      searchResults: [],
    }
  },
  watch: {
    ids() {
      if (this.multiple) {
        this.$emit('input', this.ids)
      } else {
        this.$emit('input', this.ids[0] || null)
      }
    },
    tagSelection() {
      this.$emit('onchange', this.tagSelection)
    },
  },
  methods: {
    close() {
      if (this.focused) this.focused = false
    },

    keyUp() {
      if (this.search.length > 2) this.searchTags()
      else if (this.search.length === 0) this.getTagOptions()
    },

    getTitle(tag) {
      if (tag && tag.l10n.find((obj) => obj.lang === this.lang))
        return tag.l10n.find((obj) => obj.lang === this.lang).name
      return tag.l10n[0].name
    },

    toggleDropdown(id) {
      if (this.activeParents.includes(id)) {
        this.activeParents = this.activeParents.filter((p) => p !== id)
      } else {
        this.activeParents.push(id)
      }
    },

    getSubtags(tag) {
      const searchParams = {
        order_by: 'alphabetical',
        order_dir: 'asc',
        types: this.types,
        lang: this.lang,
      }

      if (!this.subTags.some((t) => t.parent_id === tag.id)) {
        this.loading = true

        HTTP.get(`tag/${tag.id}`, searchParams).then((response) => {
          if (response.data.subTags.length > 0) {
            response.data.subTags.map((t) => this.subTags.push(t))
          }
          this.loading = false
        })
      }
    },

    searchTags: debounce(function () {
      if (this.search.length > 2) {
        this.loading = true

        const searchParams = {
          search_query: this.search,
          order_by: 'alphabetical',
          order_dir: 'asc',
          types: this.types,
          lang: this.lang,
          parent_tag: true,
        }

        HTTP.post('search/tag', searchParams).then((response) => {
          response.data.tags.map((t) => {
            if (!this.tagSelection.map((t) => t.id).includes(t.id)) {
              this.searchResults.push(t)
            }
          })

          if (this.searchResults.length === 0) {
            this.noResults = true
          } else {
            this.noResults = false
          }

          this.loading = false
        })
      }
    }, 500),

    // Tag actions
    getTagOptions: debounce(function () {
      this.loading = true

      const searchParams = {
        // search_query: this.search,
        order_by: 'alphabetical',
        order_dir: 'asc',
        types: this.types,
        lang: this.lang,
        only_root: true,
        sub_tags: true,
      }

      // if (this.search.length === 0 && this.limit) searchParams.limit = this.limit

      HTTP.post('search/tag', searchParams).then((response) => {
        this.loading = false
        this.tagOptions = response.data.tags
        this.searchResults = []
        if (this.tagOptions.length === 0) {
          this.noResults = true
        } else {
          this.noResults = false
        }
      })
    }, 500),

    pushTag(tag, subTag) {
      if (subTag && !this.tagSelection.some((t) => t.id === subTag.id)) {
        this.ids.push(subTag.id)
        this.tagSelection.push(subTag)
      }
      if (!this.tagSelection.some((t) => t.id === tag.id)) {
        this.ids.push(tag.id)
        this.tagSelection.push(tag)

        if (!this.isParent(tag) && !this.ids.includes(tag.parent_id)) {
          this.ids.push(tag.parent_id)
          this.tagSelection.push(tag.parentTag)
        }
      }
      this.focused = false
      this.search = ''
    },

    isParent(tag) {
      return !tag.parent_id
    },

    removeTag(tag) {
      if (this.isParent(tag)) {
        // add to subTags if removed
        // this.tagSelection.map(t => {
        //   if (t.parent_id === tag.id) this.subTags.push(t)
        // })
        this.tagSelection = this.tagSelection.filter((t) => t.parent_id !== tag.id)
        this.ids = this.tagSelection.map((t) => t.id)
      }

      const index = this.tagSelection.findIndex((x) => x.id === tag.id)
      this.ids.splice(index, 1)
      this.tagSelection.splice(index, 1)
    },
  },

  created() {
    if (this.defaultTags && this.user.org_id === 7 && this.action === 'create') {
      HTTP.post(`search/tag`, { ids: [this.defaultTags] }).then((response) => {
        this.pushTag(response.data.tags[0])
      })
    }

    if (typeof this.tags === 'object' && !Array.isArray(this.tags) && this.tags !== null) {
      this.tagSelection = [this.tags]
      this.ids = [this.tags.id]
    } else if (this.tags && this.tags.length > 0 && typeof this.tags[0] === 'object') {
      this.tagSelection = this.tags
      this.ids = this.tags.map((tag) => tag.id)
    } else if (this.tags && this.tags.length > 0 && typeof this.tags[0] === 'number') {
      this.ids = this.tags
      HTTP.post(`search/tag`, { ids: this.tags }).then((response) => {
        response.data.tags.forEach((tag) => {
          this.tagSelection.push(tag)
        })
      })
    }
  },
}
</script>
